import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchFeedbacksByUser, getCustomerPurchasedSkus } from '../services/api';
import { getCustomer } from '../services/shopify';
import { getCurrentUserId } from '../utils/feedbackStorage';

// Cache constants for purchased SKUs
const PURCHASED_SKUS_CACHE_KEY = 'purchasedSkusCache';
const PURCHASED_SKUS_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Get cached purchased SKUs from localStorage
 * @param {string} customerId - The Shopify customer ID
 * @returns {string[] | null} - Cached SKU prefixes or null if cache is invalid/expired
 */
const getCachedPurchasedSkus = (customerId) => {
  try {
    const cacheStr = localStorage.getItem(PURCHASED_SKUS_CACHE_KEY);
    if (!cacheStr) return null;

    const cache = JSON.parse(cacheStr);
    
    // Check if cache is for the same customer and not expired
    if (
      cache.customerId === customerId &&
      cache.timestamp &&
      Date.now() - cache.timestamp < PURCHASED_SKUS_CACHE_TTL
    ) {
      return cache.skuPrefixes;
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading purchased SKUs cache:', error);
    return null;
  }
};

/**
 * Save purchased SKUs to localStorage cache
 * @param {string} customerId - The Shopify customer ID
 * @param {string[]} skuPrefixes - Array of SKU prefixes (first 7 chars)
 */
const cachePurchasedSkus = (customerId, skuPrefixes) => {
  try {
    const cache = {
      customerId,
      skuPrefixes,
      timestamp: Date.now(),
    };
    localStorage.setItem(PURCHASED_SKUS_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Error caching purchased SKUs:', error);
  }
};

/**
 * Clear the purchased SKUs cache (used when refreshing)
 */
const clearPurchasedSkusCache = () => {
  try {
    localStorage.removeItem(PURCHASED_SKUS_CACHE_KEY);
  } catch (error) {
    console.warn('Error clearing purchased SKUs cache:', error);
  }
};

// The feedback API has no delete route (POST is an upsert on
// user/variant/order and the rating column rejects 0/null), so "unliking" a
// product can only be removed client-side. Dismissed rating keys are stored
// per-user in localStorage so the removal survives reloads; rating the
// product again clears the dismissal and the server upsert takes over.
const DISMISSED_RATINGS_KEY = 'dismissedProductRatings';

const readDismissedRatingKeys = (userId) => {
  try {
    const raw = localStorage.getItem(DISMISSED_RATINGS_KEY);
    const all = raw ? JSON.parse(raw) : {};
    return new Set(Array.isArray(all[userId]) ? all[userId] : []);
  } catch (error) {
    return new Set();
  }
};

const writeDismissedRatingKeys = (userId, keySet) => {
  try {
    const raw = localStorage.getItem(DISMISSED_RATINGS_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[userId] = Array.from(keySet);
    localStorage.setItem(DISMISSED_RATINGS_KEY, JSON.stringify(all));
  } catch (error) {
    console.warn('Error saving dismissed ratings:', error);
  }
};

const ratingKeysFor = (variantId, sku) => [
  variantId ? `variant_${variantId}` : null,
  sku ? `sku_${sku.toUpperCase()}` : null,
].filter(Boolean);

const ProductRatingContext = createContext({
  ratings: {},
  isLoading: true,
  purchasedSkus: new Set(), // Stores first 7 chars of SKUs (e.g., "WG_SDRL")
  purchasedSkusLoading: true,
  refreshRatings: () => {},
  refreshPurchasedSkus: () => {},
  updateLocalRating: () => {},
  removeLocalRating: () => {},
  getRating: () => null,
  hasPurchased: () => false,
});

/**
 * ProductRatingProvider - Context provider for managing product ratings
 * Loads and caches all product ratings for the current user
 */
export const ProductRatingProvider = ({ children }) => {
  const [ratings, setRatings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [purchasedSkus, setPurchasedSkus] = useState(new Set());
  const [purchasedSkusLoading, setPurchasedSkusLoading] = useState(true);

  // Load all product ratings for current user
  const loadRatings = useCallback(async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      const feedbacks = await fetchFeedbacksByUser('product', userId);
      const ratingsMap = {};

      feedbacks.forEach((fb) => {
        // Create keys for both variant ID and SKU lookup
        const variantId = fb.variantId || fb.variant_id || fb.shopify_variant_id;
        const sku = (fb.sku || '').toUpperCase();

        const ratingData = {
          rating: fb.rating,
          comment: fb.comment,
          createdAt: fb.createdAt || fb.created_at,
        };

        // A product can have several feedback rows (quick ratings have a
        // null order_id, so the backend upsert never merges them); keep the
        // most recent one so an adjusted rating wins over older submissions.
        const assignIfNewer = (key) => {
          const existing = ratingsMap[key];
          if (
            !existing ||
            new Date(ratingData.createdAt || 0) >= new Date(existing.createdAt || 0)
          ) {
            ratingsMap[key] = ratingData;
          }
        };

        if (variantId) {
          assignIfNewer(`variant_${variantId}`);
        }
        if (sku) {
          assignIfNewer(`sku_${sku}`);
        }
      });

      // Hide ratings the user removed locally (no server-side delete exists).
      readDismissedRatingKeys(userId).forEach((key) => {
        delete ratingsMap[key];
      });

      setRatings(ratingsMap);
    } catch (error) {
      console.error('Error loading product ratings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load purchased SKUs for current user (uses Shopify customer ID with caching)
  const loadPurchasedSkus = useCallback(async () => {
    try {
      // Get Shopify customer access token from localStorage
      const tokenStr = localStorage.getItem('shopifyCustomerAccessToken');
      if (!tokenStr) {
        setPurchasedSkusLoading(false);
        return;
      }

      const tokenObj = JSON.parse(tokenStr);
      if (!tokenObj?.accessToken) {
        setPurchasedSkusLoading(false);
        return;
      }

      // Fetch Shopify customer to get their ID
      const customer = await getCustomer(tokenObj.accessToken);
      if (!customer?.id) {
        setPurchasedSkusLoading(false);
        return;
      }

      // Extract numeric ID from Shopify GID format (gid://shopify/Customer/123456)
      const shopifyCustomerId = (customer.id || '').split('/').pop();
      if (!shopifyCustomerId) {
        setPurchasedSkusLoading(false);
        return;
      }

      // Check cache first
      const cachedSkuPrefixes = getCachedPurchasedSkus(shopifyCustomerId);
      if (cachedSkuPrefixes) {
        setPurchasedSkus(new Set(cachedSkuPrefixes));
        setPurchasedSkusLoading(false);
        return;
      }

      // Cache miss - fetch from API
      const skus = await getCustomerPurchasedSkus(shopifyCustomerId);
      // Store first 7 characters of each SKU (product identifier without supplier suffix)
      // e.g., "WG_SDRL_SL" and "WG_SDRL_CO" are the same product from different suppliers
      const skuPrefixes = skus.map((sku) => (sku || '').toUpperCase().substring(0, 7));
      const skuPrefixSet = new Set(skuPrefixes);
      
      // Cache the results
      cachePurchasedSkus(shopifyCustomerId, skuPrefixes);
      
      setPurchasedSkus(skuPrefixSet);
    } catch (error) {
      console.error('Error loading purchased SKUs:', error);
    } finally {
      setPurchasedSkusLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadRatings();
    loadPurchasedSkus();
  }, [loadRatings, loadPurchasedSkus]);

  // Refresh ratings (e.g., after submitting a new rating)
  const refreshRatings = useCallback(() => {
    setIsLoading(true);
    loadRatings();
  }, [loadRatings]);

  // Refresh purchased SKUs (clears cache and reloads from API)
  const refreshPurchasedSkus = useCallback(() => {
    clearPurchasedSkusCache();
    setPurchasedSkusLoading(true);
    loadPurchasedSkus();
  }, [loadPurchasedSkus]);

  // Update local rating without API call (for immediate UI feedback)
  const updateLocalRating = useCallback((variantId, sku, rating) => {
    const keys = ratingKeysFor(variantId, sku);

    setRatings((prev) => {
      const updated = { ...prev };
      const ratingData = { rating, createdAt: new Date().toISOString() };
      keys.forEach((key) => {
        updated[key] = ratingData;
      });
      return updated;
    });

    // Rating again cancels a previous local removal for this product.
    const userId = getCurrentUserId();
    if (userId) {
      const dismissed = readDismissedRatingKeys(userId);
      const hadAny = keys.some((key) => dismissed.has(key));
      if (hadAny) {
        keys.forEach((key) => dismissed.delete(key));
        writeDismissedRatingKeys(userId, dismissed);
      }
    }
  }, []);

  // Remove a rating locally ("unlike"). The feedback API has no delete
  // route, so the removal is client-side only and persisted per-user in
  // localStorage; see DISMISSED_RATINGS_KEY above.
  const removeLocalRating = useCallback((variantId, sku) => {
    const keys = ratingKeysFor(variantId, sku);
    if (keys.length === 0) return;

    setRatings((prev) => {
      const updated = { ...prev };
      keys.forEach((key) => {
        delete updated[key];
      });
      return updated;
    });

    const userId = getCurrentUserId();
    if (userId) {
      const dismissed = readDismissedRatingKeys(userId);
      keys.forEach((key) => dismissed.add(key));
      writeDismissedRatingKeys(userId, dismissed);
    }
  }, []);

  // Get rating for a product by variant ID or SKU
  const getRating = useCallback(
    (variantId, sku) => {
      if (variantId && ratings[`variant_${variantId}`]) {
        return ratings[`variant_${variantId}`].rating;
      }
      if (sku && ratings[`sku_${sku.toUpperCase()}`]) {
        return ratings[`sku_${sku.toUpperCase()}`].rating;
      }
      return null;
    },
    [ratings]
  );

  // Check if customer has purchased a product by SKU prefix (first 7 chars)
  // This allows matching products from different suppliers (e.g., WG_SDRL_SL and WG_SDRL_CO)
  const hasPurchased = useCallback(
    (sku) => {
      if (!sku) return false;
      const skuPrefix = sku.toUpperCase().substring(0, 7);
      return purchasedSkus.has(skuPrefix);
    },
    [purchasedSkus]
  );

  return (
    <ProductRatingContext.Provider
      value={{
        ratings,
        isLoading,
        purchasedSkus,
        purchasedSkusLoading,
        refreshRatings,
        refreshPurchasedSkus,
        updateLocalRating,
        removeLocalRating,
        getRating,
        hasPurchased,
      }}
    >
      {children}
    </ProductRatingContext.Provider>
  );
};

/**
 * useProductRatings - Hook to access product ratings context
 */
export const useProductRatings = () => {
  const context = useContext(ProductRatingContext);
  if (!context) {
    console.warn('useProductRatings must be used within a ProductRatingProvider');
    return {
      ratings: {},
      isLoading: false,
      purchasedSkus: new Set(),
      purchasedSkusLoading: false,
      refreshRatings: () => {},
      refreshPurchasedSkus: () => {},
      updateLocalRating: () => {},
      removeLocalRating: () => {},
      getRating: () => null,
      hasPurchased: () => false,
    };
  }
  return context;
};

export default ProductRatingContext;

